// Columna derecha del explorer (`.col-detail` del mockup): hero + acciones + cuatro pestañas.
//
// La pestaña Cliente se dibuja aunque no pueda llenarse del todo, y cada hueco dice su porqué:
// esconderla haría creer que la ficha está completa.
import { ArrowUpRight, Ban, Clock, RotateCw, X } from 'lucide-react';
import { Button, ConfirmDialog, Skeleton } from '@/kit';
import { MasterDetailEmpty } from '@/shared/patterns/MasterDetail';
import { StatusPill, TierPill } from '@/shared/patterns/Pills';
import { cn } from '@/lib/cn';
import { formatDateMx, formatShortDateMx } from '@/lib/dateHelpers';
import { formatMXN } from '@/shared/lib/format/money';
import type { Customer360 } from '@/api/services/retail-customer';
import { blockedCategoryText, blockedSinceText } from '@/domains/sales/wizard/blocked';
import { prospectPhone, prospectReasonText } from '@/domains/sales/wizard/prospects';
import { stepById } from '@/domains/sales/wizard/steps';
import type { ActiveWizardSession, CatalogItem, WizardSession } from '@/domains/sales/wizard/types';
import { modelLabel } from '../../catalog/lib/catalogExplorer';
import type { CustomerCreditFacts } from '../../customers/lib/customerCreditFacts';
import type { BorrowerRejectionsFacts } from '../../customers/lib/sciRejections';
import {
    CREDIT_ELSEWHERE_LABEL,
    formatPhone,
    sciSourceNote,
    tierMissingNote,
} from '../../customers/lib/customersFormat';
import {
    createdIso,
    CREDIT_HISTORY_NOTE,
    CREDIT_NO_CUSTOMER_NOTE,
    creditUnreadNote,
    CUSTOMER_360_NO_CUSTOMER_NOTE,
    isSettled,
    NO_CONTACT_TEXT,
    PENDING_CELL,
    prospectTimeline,
    REJECTIONS_UNREAD_NOTE,
    savedSaleNotice,
    SETTLED_REASON_LABEL,
    SETTLED_REASON_NOTE,
    shortFolio,
    statusChipClass,
    statusLabel,
    WIZARD_ACTIVE_SESSION_NOTE,
    WIZARD_BLOCKED_REOPEN_NOTE,
    WIZARD_LINKED_LOOKUP_FAILED_NOTE,
    WIZARD_LINKED_STEP_NOTE,
    WIZARD_LOOKUP_FAILED_NOTE,
    WIZARD_LOOKUP_OFF_NOTE,
    moneyAtStakeBadge,
    WIZARD_NO_SESSION_NOTE,
    WIZARD_NO_SOURCE_NOTE,
    WIZARD_SETTLED_STEP_NOTE,
    type EventTone,
} from '../lib/prospectsFormat';
import type { ProspectRow } from '../lib/prospectsRows';
import { savedScreenLabel } from '../lib/prospectsSavedScreen';

/**
 * La venta que la ficha guarda en su `wizardSessionId`, leída POR ID. Es INFORMATIVA: dice en qué
 * paso quedó y no habilita reabrir.
 */
export interface LinkedWizardInfo {
    /** La venta de la que salió la ficha. `null` cuando la ficha no guarda ninguna. */
    sessionId: string | null;
    /** Ausente con la lectura en vuelo y también cuando falló: en los dos casos no hay paso. */
    session: WizardSession | undefined;
    isLoading: boolean;
    /** La ficha ya salió de la agenda: el paso es historia, no una venta que retomar. */
    settled: boolean;
}

/** Lo que la pestaña Wizard necesita para elegir entre sus desenlaces. */
export interface WizardTabInfo {
    session: ActiveWizardSession | undefined;
    isLoading: boolean;
    /** La consulta falló. La venta puede existir igual: no se afirma que no haya. */
    isError: boolean;
    /** Con la búsqueda apagada no se puede afirmar que no haya venta abierta, solo que no se miró. */
    lookupEnabled: boolean;
    /** Presente cuando el paso se lee por id: la ficha resuelta y la abierta sin teléfono. */
    linked?: LinkedWizardInfo;
}

/** El historial de crédito del cliente del prospecto. Accesorio: su fallo no rompe la ficha. */
export interface ProspectCreditInfo {
    /** El prospecto ya está registrado como cliente. Sin eso no hay expediente que consultar. */
    hasCustomer: boolean;
    /** Las cifras de su cartera, o el motivo por el que no las hay. Nunca las dos cosas. */
    facts: CustomerCreditFacts;
    /** Sus rechazos del scoring en la ventana reciente, o el motivo del hueco. */
    rejections: BorrowerRejectionsFacts;
}

export const DETAIL_TABS = ['prospecto', 'cliente', 'wizard', 'bitacora'] as const;
export type DetailTab = (typeof DETAIL_TABS)[number];

const TAB_LABEL: Record<DetailTab, string> = {
    prospecto: 'Prospecto',
    cliente: 'Cliente',
    wizard: 'Wizard',
    bitacora: 'Bitácora',
};

const CHIP =
    'inline-flex items-center rounded-full border px-1.5 py-0.5 text-[9.5px] font-semibold';

const SECTION_TITLE =
    'mb-1.5 border-b border-border pb-1 text-[10px] font-semibold uppercase tracking-[0.05em] text-muted-foreground';

const TIMELINE_DOT: Record<EventTone, string> = {
    info: 'bg-info',
    success: 'bg-success',
    danger: 'bg-destructive',
    muted: 'bg-muted-foreground',
};

/** Ata el botón apagado con el párrafo que dice por qué, para el lector de pantalla. */
const NO_CUSTOMER_360_ID = 'prospect-no-customer-360';

/** Por qué el botón de reabrir está apagado: la venta ya se resolvió, no hay nada que retomar. */
const SETTLED_NOTE = 'Este prospecto ya se resolvió: no hay venta que retomar.';

export function ProspectDetailPanel({
    row,
    product,
    customer,
    isCustomerLoading,
    credit,
    wizard,
    tab,
    onTabChange,
    onReopen,
    isReopening = false,
    onOpenCustomer,
    canOpenCustomer,
    onMarkLost,
    isMarkingLost,
    confirmingLost,
    onConfirmingLostChange,
    onClose,
}: {
    row: ProspectRow | undefined;
    /** Ficha del catálogo del SKU de interés, si el catálogo cargó y lo tiene. */
    product: CatalogItem | undefined;
    /** Cliente 360 del prospecto identificado. BEST-EFFORT: puede no llegar nunca. */
    customer: Customer360 | undefined;
    isCustomerLoading: boolean;
    /** Historial de crédito del cliente del prospecto. Accesorio: su fallo no rompe la ficha. */
    credit: ProspectCreditInfo;
    /** Venta en curso del teléfono del prospecto. Accesoria: su fallo no rompe la ficha. */
    wizard: WizardTabInfo;
    tab: DetailTab;
    onTabChange: (tab: DetailTab) => void;
    onReopen: () => void;
    /** «Reabrir» espera al silo, o a la ficha 360 que todavía puede traer el teléfono. */
    isReopening?: boolean;
    /** Salta a Clientes con la ficha 360 de este prospecto abierta. Solo si ya es cliente. */
    onOpenCustomer: () => void;
    /** El vendedor puede entrar a Clientes. Sin el permiso la acción no se ofrece: la pantalla
        destino le respondería un 403 sin explicación. */
    canOpenCustomer: boolean;
    onMarkLost: () => void;
    isMarkingLost: boolean;
    confirmingLost: boolean;
    onConfirmingLostChange: (open: boolean) => void;
    /** Cierra la ficha sin tocar el prospecto. */
    onClose: () => void;
}) {
    if (!row) {
        return (
            <MasterDetailEmpty
                icon={<Clock className="size-8" aria-hidden />}
                title="Selecciona un prospecto para ver su detalle"
                sub="Elige una fila de la tabla para ver su contexto, su cliente y su bitácora."
            />
        );
    }

    const prospect = row.prospect;
    const settled = isSettled(prospect.status);
    const reason = prospectReasonText(prospect);

    return (
        <>
            {/* .detail-hero */}
            <div className="flex-none border-b border-border bg-muted/20 px-3.5 py-3">
                <div className="mb-1 flex items-start justify-between gap-2">
                    <span className="text-[9.5px] font-semibold uppercase tracking-wider text-muted-foreground">
                        Prospecto
                    </span>
                    <div className="flex items-center gap-1">
                        <span className={cn(CHIP, statusChipClass(prospect.status))}>
                            {statusLabel(prospect.status)}
                        </span>
                        <Button
                            size="icon"
                            variant="ghost"
                            className="size-6"
                            aria-label="Cerrar ficha"
                            title="Cerrar ficha"
                            onClick={onClose}
                        >
                            <X className="size-3.5" aria-hidden />
                        </Button>
                    </div>
                </div>
                <div
                    className="font-mono text-[15px] font-semibold leading-tight"
                    title={row.folio}
                >
                    {shortFolio(row.folio)}
                </div>
                <div className="mt-1 flex flex-wrap items-center gap-1.5">
                    {/* Resuelta, el motivo es historia: en ámbar contradice al estado de al lado. */}
                    <span
                        className={cn(
                            CHIP,
                            settled
                                ? 'border-border text-muted-foreground'
                                : 'border-warning text-warning',
                        )}
                        title={settled ? SETTLED_REASON_NOTE : undefined}
                    >
                        {reason}
                    </span>
                    <span className={cn(CHIP, 'border-border text-muted-foreground')}>
                        Próx. {formatShortDateMx(prospect.nextContactAt)}
                    </span>
                </div>
                <SavedSaleLine wizard={wizard} />
                <div className="mt-2 text-[11.5px]">
                    <span className="font-medium">{row.customerName}</span>
                    <span className="mx-1 text-muted-foreground">·</span>
                    <span className="text-[10.5px] text-muted-foreground">{row.productLabel}</span>
                </div>
            </div>

            {/* .detail-actions */}
            <div className="flex flex-none gap-1.5 border-b border-border px-3.5 py-2.5">
                <Button
                    size="sm"
                    className="flex-1"
                    disabled={settled || isReopening}
                    title={settled ? SETTLED_NOTE : undefined}
                    leftIcon={<RotateCw className="size-3" aria-hidden />}
                    onClick={onReopen}
                >
                    Reabrir wizard
                </Button>
                <Button
                    size="sm"
                    variant="outline"
                    className="border-destructive/40 text-destructive hover:bg-destructive/10 hover:text-destructive"
                    disabled={settled || isMarkingLost}
                    leftIcon={<Ban className="size-3" aria-hidden />}
                    onClick={() => {
                        onConfirmingLostChange(true);
                    }}
                >
                    Marcar como perdido
                </Button>
            </div>

            {/* .detail-tabs */}
            <div
                role="tablist"
                className="flex flex-none gap-1 overflow-x-auto border-b border-border px-3"
            >
                {DETAIL_TABS.map((key) => (
                    <button
                        key={key}
                        type="button"
                        role="tab"
                        aria-selected={tab === key}
                        onClick={() => {
                            onTabChange(key);
                        }}
                        className={cn(
                            '-mb-px whitespace-nowrap border-b-2 px-2.5 py-1.5 text-[11px] transition-colors',
                            tab === key
                                ? 'border-primary font-semibold text-foreground'
                                : 'border-transparent font-medium text-muted-foreground hover:text-foreground',
                        )}
                    >
                        {TAB_LABEL[key]}
                    </button>
                ))}
            </div>

            <div className="min-h-0 flex-1 overflow-auto px-3.5 py-3 text-[12px]">
                {tab === 'prospecto' ? (
                    <ProspectTab row={row} product={product} settled={settled} />
                ) : tab === 'cliente' ? (
                    <CustomerTab
                        row={row}
                        customer={customer}
                        isLoading={isCustomerLoading}
                        credit={credit}
                        onOpenCustomer={onOpenCustomer}
                        canOpenCustomer={canOpenCustomer}
                    />
                ) : tab === 'wizard' ? (
                    <WizardTab wizard={wizard} />
                ) : (
                    <TimelineTab row={row} />
                )}
            </div>

            <ConfirmDialog
                open={confirmingLost}
                onOpenChange={onConfirmingLostChange}
                title="Marcar prospecto como perdido"
                description="¿Confirmas que este prospecto no se cerrará como venta? El vendedor perderá el follow-up asociado."
                confirmLabel="Marcar perdido"
                destructive
                loading={isMarkingLost}
                onConfirm={onMarkLost}
            />
        </>
    );
}

/** Hasta cuándo se retoma la venta guardada, a la vista sin cambiar de pestaña. Sin dato, no afirma nada. */
function SavedSaleLine({ wizard }: { wizard: WizardTabInfo }) {
    const session = wizard.session;
    if (wizard.isLoading || session?.found !== true) return null;
    const notice = session.exactUntil !== undefined ? savedSaleNotice(session.exactUntil) : null;
    const money = moneyAtStakeBadge(session.moneyAtStakeKind);
    if (!notice && !money) return null;
    return (
        <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
            {notice ? (
                <span
                    className={cn(
                        'inline-flex items-center gap-1 text-[11px] font-medium',
                        notice.expired ? 'text-warning' : 'text-info',
                    )}
                >
                    <Clock className="size-3 flex-none" aria-hidden />
                    {notice.text}
                </span>
            ) : null}
            {money ? (
                <span className={cn(CHIP, 'border-warning text-warning')} title={money.note}>
                    {money.label}
                </span>
            ) : null}
        </div>
    );
}

function InfoRow({ label, value }: { label: string; value: React.ReactNode }) {
    return (
        <div className="flex items-baseline justify-between gap-3 py-[3px] text-[11.5px]">
            <span className="flex-none text-muted-foreground">{label}</span>
            <span className="break-words text-right font-medium">{value}</span>
        </div>
    );
}

/** Fila cuyo dato no tiene fuente todavía: se ve, en guion, y con su porqué al pasar el mouse. */
function PendingRow({ label, note }: { label: string; note: string }) {
    return (
        <div
            title={note}
            className="flex items-baseline justify-between gap-3 py-[3px] text-[11.5px] opacity-60"
        >
            <span className="flex-none text-muted-foreground">{label}</span>
            <span className="text-muted-foreground">{PENDING_CELL}</span>
        </div>
    );
}

function ProspectTab({
    row,
    product,
    settled,
}: {
    row: ProspectRow;
    product: CatalogItem | undefined;
    /** La ficha ya salió de la agenda: su motivo se lee como historia, no como pendiente. */
    settled: boolean;
}) {
    const prospect = row.prospect;
    return (
        <>
            <section className="mb-3.5">
                <h4 className={SECTION_TITLE}>Contexto del prospecto</h4>
                {/* Aquí va COMPLETO: es de donde se copia el folio para buscarlo o reportarlo. */}
                <InfoRow
                    label="Folio"
                    value={<span className="break-all font-mono">{row.folio}</span>}
                />
                <InfoRow label="Creado" value={formatDateMx(createdIso(prospect))} />
                <InfoRow
                    label={settled ? SETTLED_REASON_LABEL : 'Motivo'}
                    value={prospectReasonText(prospect)}
                />
                <InfoRow label="Próximo contacto" value={formatDateMx(prospect.nextContactAt)} />
                <InfoRow
                    label="Estado"
                    value={
                        <span className={cn(CHIP, statusChipClass(prospect.status))}>
                            {statusLabel(prospect.status)}
                        </span>
                    }
                />
                <InfoRow
                    label="Último contacto"
                    value={
                        prospect.lastContactAt
                            ? formatDateMx(prospect.lastContactAt)
                            : NO_CONTACT_TEXT
                    }
                />
                {settled ? (
                    <p className="mt-1 text-[10.5px] leading-relaxed text-muted-foreground">
                        {SETTLED_REASON_NOTE}
                    </p>
                ) : null}
            </section>

            <section className="mb-3.5">
                <h4 className={SECTION_TITLE}>Producto de interés</h4>
                {prospect.productSku ? (
                    <>
                        <InfoRow
                            label="SKU"
                            value={<span className="font-mono">{prospect.productSku}</span>}
                        />
                        <InfoRow label="Marca" value={product?.brand || PENDING_CELL} />
                        <InfoRow
                            label="Modelo"
                            value={(product && modelLabel(product)) || PENDING_CELL}
                        />
                        <InfoRow
                            label="Precio"
                            value={product ? formatMXN(product.cashPriceCents / 100) : PENDING_CELL}
                        />
                        {product ? null : (
                            <p className="mt-1 text-[10.5px] leading-relaxed text-muted-foreground">
                                El catálogo de la tienda no trae este SKU: puede haber salido de
                                venta.
                            </p>
                        )}
                    </>
                ) : (
                    <p className="text-[11px] italic text-muted-foreground">
                        El cliente aún no eligió producto.
                    </p>
                )}
            </section>

            <section>
                <h4 className={SECTION_TITLE}>Notas del vendedor</h4>
                <div className="rounded-md border border-border bg-muted/30 p-2 text-[11.5px]">
                    {prospect.notes ?? 'Sin notas.'}
                </div>
            </section>
        </>
    );
}

/**
 * El Cliente 360 es BEST-EFFORT: el rol `vendedor` puede no tener todavía `retail.customer.list`.
 * Cuando no llega, la pestaña muestra lo que el propio prospecto sabe en vez de romperse.
 */
function CustomerTab({
    row,
    customer,
    isLoading,
    credit,
    onOpenCustomer,
    canOpenCustomer,
}: {
    row: ProspectRow;
    customer: Customer360 | undefined;
    isLoading: boolean;
    credit: ProspectCreditInfo;
    onOpenCustomer: () => void;
    canOpenCustomer: boolean;
}) {
    const prospect = row.prospect;
    const identified = prospect.retailCustomerId !== null;
    const summary = customer?.customer;
    const phone = prospectPhone(prospect) ?? summary?.phone;

    return (
        <>
            <section className="mb-3.5">
                <h4 className={SECTION_TITLE}>Datos del cliente</h4>
                {isLoading ? (
                    <Skeleton lines={4} />
                ) : (
                    <>
                        <InfoRow label="Nombre" value={row.customerName} />
                        <InfoRow
                            label="Cliente"
                            value={identified ? 'Registrado en Fiado' : 'Cliente nuevo'}
                        />
                        <InfoRow label="Teléfono" value={formatPhone(phone)} />
                        <CreditLevelRow credit={credit} />
                    </>
                )}
                {identified && !isLoading && !summary ? (
                    <p className="mt-1 text-[10.5px] leading-relaxed text-muted-foreground">
                        No se pudo leer la ficha del cliente. Se muestra lo que el prospecto guardó.
                    </p>
                ) : null}
                {canOpenCustomer ? (
                    <>
                        <Button
                            size="sm"
                            variant="outline"
                            fullWidth
                            className="mt-2"
                            disabled={!identified}
                            aria-describedby={identified ? undefined : NO_CUSTOMER_360_ID}
                            leftIcon={<ArrowUpRight className="size-3" aria-hidden />}
                            onClick={onOpenCustomer}
                        >
                            Ver ficha 360°
                        </Button>
                        {identified ? null : (
                            <p
                                id={NO_CUSTOMER_360_ID}
                                className="mt-1 text-[10.5px] leading-relaxed text-muted-foreground"
                            >
                                {CUSTOMER_360_NO_CUSTOMER_NOTE}
                            </p>
                        )}
                    </>
                ) : null}
            </section>

            <section>
                <h4 className={SECTION_TITLE}>Historial con SureKeep</h4>
                <CreditHistoryRows credit={credit} />
                <RejectionsRow rejections={credit.rejections} />
                {/* El cierre dice de dónde salen los créditos: al lado de «no se pudieron leer»
                    se contradice con la nota del estado, que ya explicó por qué no hay cifras. */}
                {credit.facts.entry ? (
                    <p className="mt-2 text-[10.5px] leading-relaxed text-muted-foreground">
                        {CREDIT_HISTORY_NOTE}
                    </p>
                ) : null}
            </section>
        </>
    );
}

/**
 * Los rechazos del scoring en los últimos 30 días. Un cero LEÍDO se pinta «0» porque es dato
 * afirmado; cada motivo de hueco —sin cliente, sin acreditado, sin permiso, lectura caída— va en
 * guion con el suyo, y ninguno de los cuatro significa que no haya habido rechazos.
 */
function RejectionsRow({ rejections }: { rejections: BorrowerRejectionsFacts }) {
    const label = 'Rechazos 30d';
    if (rejections.unreadState === 'loading') return <Skeleton lines={1} />;
    if (rejections.unreadState !== null) {
        return <PendingRow label={label} note={REJECTIONS_UNREAD_NOTE[rejections.unreadState]} />;
    }
    return <InfoRow label={label} value={String(rejections.count)} />;
}

/** El tier de CRÉDITO — Bronce/Plata/Oro —, no el nivel de la cuenta PagoConFiado. Sale de la misma
    cartera que los conteos de abajo, así que hereda sus mismos huecos con motivo. */
function CreditLevelRow({ credit }: { credit: ProspectCreditInfo }) {
    const label = 'Nivel de crédito';
    if (!credit.hasCustomer) return <PendingRow label={label} note={CREDIT_NO_CUSTOMER_NOTE} />;
    const { entry, unreadState } = credit.facts;
    if (unreadState === 'loading') return <Skeleton lines={1} />;
    if (!entry) return <PendingRow label={label} note={creditUnreadNote(unreadState)} />;
    // El scoring nunca dejó nivel: es ausencia de dato, no un «no elegible».
    if (entry.clientLevel === null)
        return <PendingRow label={label} note={tierMissingNote(entry.creditsCount)} />;
    return (
        <InfoRow
            label={label}
            value={
                <span className="inline-flex items-center gap-1.5">
                    <TierPill level={entry.clientLevel} title={sciSourceNote(entry.sciSource)} />
                    {entry.hasActiveCreditElsewhere ? (
                        <StatusPill tone="info">{CREDIT_ELSEWHERE_LABEL}</StatusPill>
                    ) : null}
                </span>
            }
        />
    );
}

/**
 * Los dos conteos del historial de crédito. Cada hueco dice el suyo: el prospecto que todavía no es
 * cliente no es lo mismo que la lectura que no se pudo hacer, y ninguno de los dos es un cero.
 */
function CreditHistoryRows({ credit }: { credit: ProspectCreditInfo }) {
    if (!credit.hasCustomer) {
        return (
            <>
                <PendingRow label="Créditos totales" note={CREDIT_NO_CUSTOMER_NOTE} />
                <PendingRow label="Créditos activos" note={CREDIT_NO_CUSTOMER_NOTE} />
            </>
        );
    }
    const { entry, unreadState } = credit.facts;
    if (unreadState === 'loading') return <Skeleton lines={2} />;
    if (!entry) {
        const note = creditUnreadNote(unreadState);
        return (
            <>
                <PendingRow label="Créditos totales" note={note} />
                <PendingRow label="Créditos activos" note={note} />
                <p className="mt-1 text-[10.5px] leading-relaxed text-muted-foreground">{note}</p>
            </>
        );
    }
    return (
        <>
            <InfoRow label="Créditos totales" value={String(entry.creditsCount)} />
            <InfoRow label="Créditos activos" value={String(entry.activeCreditsCount)} />
        </>
    );
}

/**
 * La ficha que no puede preguntar por teléfono lee su venta POR ID (`wizardSessionId`): la resuelta,
 * porque ya no hay venta viva que buscar, y la abierta sin teléfono, porque no tiene la llave.
 */
function LinkedWizardTab({ linked }: { linked: LinkedWizardInfo }) {
    // Sobre una sesión leída el paso SIEMPRE resuelve (`toStepId` degrada al 01), así que sin paso
    // es que no llegaron datos: la lectura falló.
    const step = linked.session ? stepById(linked.session.currentStep) : undefined;
    // La ficha sin sesión vinculada no es una lectura fallida: nunca hubo venta que leer.
    const note = !linked.sessionId
        ? WIZARD_NO_SOURCE_NOTE
        : !step
          ? WIZARD_LINKED_LOOKUP_FAILED_NOTE
          : linked.settled
            ? WIZARD_SETTLED_STEP_NOTE
            : WIZARD_LINKED_STEP_NOTE;

    return (
        <>
            <section className="mb-3.5">
                <h4 className={SECTION_TITLE}>Estado del wizard</h4>
                {linked.isLoading ? (
                    <Skeleton lines={2} />
                ) : step ? (
                    <InfoRow
                        label="Paso donde quedó"
                        value={savedScreenLabel(linked.session) ?? PENDING_CELL}
                    />
                ) : (
                    <PendingRow label="Paso donde quedó" note={note} />
                )}
            </section>
            {linked.isLoading ? null : (
                <p className="rounded-md bg-info-soft px-2.5 py-2 text-[11px] leading-relaxed text-info">
                    {note}
                </p>
            )}
        </>
    );
}

/**
 * La venta VIVA de un teléfono se busca por TELÉFONO (`GET /wizard/sessions?phone=`). Cada desenlace
 * dice el suyo — no hay venta, o no se pudo consultar — porque se ven igual en pantalla y significan
 * cosas distintas.
 */
function WizardTab({ wizard }: { wizard: WizardTabInfo }) {
    if (wizard.linked) return <LinkedWizardTab linked={wizard.linked} />;
    const savedScreen = savedScreenLabel(wizard.session);
    const found = wizard.session?.found === true;
    const money = found ? moneyAtStakeBadge(wizard.session?.moneyAtStakeKind) : null;
    // Capa 2 de la venta trabada: la categoría dice quién resuelve, sin nombrar la causa.
    const blocked = found ? wizard.session?.blocked : undefined;
    const note = !wizard.lookupEnabled
        ? WIZARD_LOOKUP_OFF_NOTE
        : wizard.isError
          ? WIZARD_LOOKUP_FAILED_NOTE
          : blocked
            ? `${blockedCategoryText(blocked.category)} ${WIZARD_BLOCKED_REOPEN_NOTE}`
            : money
              ? money.note
              : found
                ? WIZARD_ACTIVE_SESSION_NOTE
                : WIZARD_NO_SESSION_NOTE;

    return (
        <>
            <section className="mb-3.5">
                <h4 className={SECTION_TITLE}>Estado del wizard</h4>
                {wizard.isLoading ? (
                    <Skeleton lines={2} />
                ) : found ? (
                    // El `sessionId` no se pinta: es un UUID y no le dice nada al vendedor (DEC-012).
                    <InfoRow label="Paso donde quedó" value={savedScreen ?? PENDING_CELL} />
                ) : (
                    <PendingRow label="Paso donde quedó" note={note} />
                )}
                {/* El «hace N días» sale de `blocked.since`, NO de `updatedAt`: cuando exista el
                    reintento va a escribir en la sesión cada vuelta y todo diría «hoy». */}
                {blocked ? (
                    <InfoRow
                        label="Venta trabada"
                        value={
                            <span className={cn(CHIP, 'border-warning text-warning')}>
                                {blockedSinceText(blocked.since)}
                            </span>
                        }
                    />
                ) : null}
            </section>
            {/* En vuelo no se afirma nada: la nota diría «no hay venta» antes de que conteste. */}
            {wizard.isLoading ? null : (
                <p
                    className={cn(
                        'rounded-md px-2.5 py-2 text-[11px] leading-relaxed',
                        wizard.isError || blocked
                            ? 'bg-warning-soft text-warning'
                            : 'bg-info-soft text-info',
                    )}
                >
                    {note}
                </p>
            )}
        </>
    );
}

function TimelineTab({ row }: { row: ProspectRow }) {
    const events = prospectTimeline(row.prospect);
    return (
        <ol className="flex flex-col">
            {events.map((event, index) => (
                <li key={`${event.at}-${event.text}`} className="relative flex gap-2 py-1.5">
                    {index < events.length - 1 ? (
                        <span
                            aria-hidden
                            className="absolute bottom-0 left-[3px] top-3 border-l border-dashed border-border"
                        />
                    ) : null}
                    <span
                        aria-hidden
                        className={cn(
                            'mt-[5px] size-2 flex-none rounded-full',
                            TIMELINE_DOT[event.tone],
                        )}
                    />
                    <div className="min-w-0 flex-1">
                        <div className="text-[11.5px]">{event.text}</div>
                        <div className="font-mono text-[10px] text-muted-foreground">
                            {formatDateMx(event.at)}
                        </div>
                    </div>
                </li>
            ))}
        </ol>
    );
}
